import React, { useState } from "react";
import { Bookmark, Share2, Mail, MailX } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import type { DiscoveryInfluencerProfile } from "../../types/discoveryProfile.types";
import { InfluencerContactModal } from "./InfluencerContactModal";

export interface ProfileHeroCardProps {
  influencer: DiscoveryInfluencerProfile;
}

const getInitials = (name: string) =>
  name.split(" ").map((n) => n[0]).join("").slice(0, 2).toUpperCase();

const formatNumber = (num: number): string => {
  if (num >= 1_000_000) return (num / 1_000_000).toFixed(1) + "M";
  if (num >= 1_000) return (num / 1_000).toFixed(1) + "K";
  return num.toString();
};

export const ProfileHeroCard: React.FC<ProfileHeroCardProps> = ({ influencer }) => {
  const [contactModalOpen, setContactModalOpen] = useState(false);

  return (
    <div className="relative -mt-32 px-6 max-w-[90%] mx-auto">
      <div
        className="rounded-[24px] p-8 flex flex-col gap-8 text-white"
        style={{
          backdropFilter: "blur(70px)",
          WebkitBackdropFilter: "blur(70px)",
        }}
      >
        {/* Top Section: Avatar, Info, and Actions */}
        <div className="flex items-start justify-between w-full">
          {/* Left Section (Avatar + Details) */}
          <div className="flex items-center gap-6">
            {/* Profile Image */}
            {influencer.profilePic ? (
              <img
                src={influencer.profilePic}
                alt={influencer.name}
                className="w-[110px] h-[110px] rounded-full object-cover flex-shrink-0"
              />
            ) : (
              <div className="w-[110px] h-[110px] rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                <span className="text-white text-3xl font-bold">
                  {getInitials(influencer.name)}
                </span>
              </div>
            )}

            {/* Details */}
            <div className="flex flex-col gap-1">
              {/* Name & Verified Badge */}
              <div className="flex items-center gap-2 flex-wrap">
                <h2 className="text-[28px] font-bold tracking-wide">
                  {influencer.name}
                </h2>
                {influencer.verified && (
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="12" cy="12" r="10" fill="#1D9BF0" />
                    <path d="M10.5 15.5L7.5 12.5L8.9 11.1L10.5 12.7L15.6 7.6L17 9L10.5 15.5Z" fill="white" />
                  </svg>
                )}
              </div>

              {/* Handle */}
              <a
                href={`https://www.instagram.com/${influencer.handle.replace(/^@/, "")}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[18px] text-white/80 hover:underline hover:text-white transition-colors"
              >
                @{influencer.handle.replace(/^@/, "")}
              </a>

              {/* Stats */}
              <div className="flex items-center gap-4 mt-3">
                {/* Followers */}
                <div className="flex items-center gap-2">
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"></path>
                    <circle cx="12" cy="7" r="4"></circle>
                  </svg>
                  <span className="text-[20px] text-white/90 font-['Inter']">
                    <span className="font-bold text-white text-[22px]">
                      {formatNumber(influencer.totalFollowers)}
                    </span>{" "}
                    Followers
                  </span>
                </div>

                {/* Vertical Divider */}
                <div className="w-[2px] h-[28px] bg-white/40 rounded-full mx-1"></div>

                {/* Engagement Rate */}
                <div className="flex items-center gap-2">
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#02FF8D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="3" width="18" height="18" rx="4"></rect>
                    <polyline points="7 14 11 10 14 13 18 9"></polyline>
                  </svg>
                  <span className="text-[20px] text-white/90 font-['Inter']">
                    <span className="font-bold text-[#02FF8D] text-[22px]">
                      {influencer.engagementRate.toFixed(2)}%
                    </span>{" "}
                    ER
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Right Section (Buttons) */}
          <div className="flex items-center gap-3">
            {/* Contact Button */}
            {influencer.hasEmail ? (
              <button
                type="button"
                onClick={() => setContactModalOpen(true)}
                className="flex items-center gap-2 px-4 h-[40px] bg-[#9333EA] rounded-[14px] text-white font-medium hover:bg-[#7e22ce] transition-colors text-sm"
              >
                <Mail size={18} />
                Contact
              </button>
            ) : (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      disabled
                      className="flex items-center gap-2 px-4 h-[40px] bg-white/20 rounded-[14px] text-white/60 font-medium text-sm cursor-not-allowed"
                    >
                      <MailX size={18} />
                      No Email
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>This influencer has not shared an email publicly.</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}

            <button type="button" className="w-[40px] h-[40px] flex items-center justify-center bg-[#EAE4F2] rounded-[14px] text-[#1A1A1A] hover:bg-white transition-colors">
              <Bookmark size={24} strokeWidth={1.5} />
            </button>
            <button type="button" className="w-[40px] h-[40px] flex items-center justify-center bg-[#EAE4F2] rounded-[14px] text-[#1A1A1A] hover:bg-white transition-colors">
              <Share2 size={24} strokeWidth={1.5} />
            </button>
          </div>
        </div>

        {/* Bottom Section: Bio */}
        {influencer.bio && (
          <div>
            <p className="text-[18px] text-white/90 italic">
              <span className="font-bold not-italic">Bio:</span> {influencer.bio}
            </p>
          </div>
        )}
      </div>

      {/* Contact Modal */}
      <InfluencerContactModal
        key={influencer.id}
        open={contactModalOpen}
        onOpenChange={setContactModalOpen}
        influencer={influencer}
      />
    </div>
  );
};

export default ProfileHeroCard;
